import React, { useContext, useEffect, useState } from 'react';
import './ExtendedReportView.scss';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';
import { MappedFilters } from '../../Portfolio_R/SummaryCharts/components/MappedFilters'
import Header from '../../Header/Header';
import { useHistory, useLocation } from 'react-router';
import usePortfolioName from '../../../hooks/usePortfolioName';
import { TagFilterContext } from '../../../context/TagFilterContext';
import { ReactComponent as IconBack } from '../../../assets/icons/u_arrow-left.svg';
import { ReactComponent as IconDownload } from '../../../assets/icons/u_download-alt.svg';
import ExtendedReportCard from './components/ExtendedReportCard/ExtendedReportCard';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './components/ExtendedReportCard/SortableItem';
import BaseModal from '../../Util/Modals/BaseModal';
import { styles } from '../../Util/Modals/Styles';
import { useDispatch, useSelector } from 'react-redux';
import SpinningLoader from '../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';
import DownloadPreviewPDF from './DownloadPreviewPDF';
import { fetchExtendedReportFromPayload } from '../../../services/extendedReportData';
import AutoDismissAlert from '../../Projects/ProjectsReport/view/Common/AlertTooltips';
import { ERROR_IN_GENERATE_REPORT, SUCESSFUL_GENERATED_PROJECT_REPORT } from '../../Projects/ProjectsReport/utils/Constants/AlertStatusConstants';
import { downloadExtendedProjectReport } from '../../Projects/ProjectsReport/utils/Downloaders/ExtendedReportDownload';
import { getSections } from '../../Projects/ProjectsReport/utils/Helpers/SectionsHelper';
import { mapInitialCheckSections } from '../../Projects/ProjectsReport/utils/Helpers/MapperHelper';
import { getLabelDownloadingReport } from '../../Projects/ProjectsReport/utils/Validators/ReportValidations';
import { setPayload } from '../../../redux/actions/indicatorPayload';

const HEADER_TITLE_SESSION_KEY = 'EXTENDED_REPORT_HEADER_TITLE';
const PROJECT_CODE_SESSION_KEY = 'EXTENDED_REPORT_PROJECT_CODE';
const PORTFOLIO_NAME_SESSION_KEY = 'ONE_PAGER_HEADER_PORTFOLIO';
const HEADER_FILTERS_SESSION_KEY = 'ONE_PAGER_HEADER_FILTERS';
const HEADER_GENERAL_DATA_SESSION_KEY = 'ONE_PAGER_HEADER_GENERAL_DATA';
const MAPPED_FILTERS_SESSION_KEY = 'ONE_PAGER_MAPPED_FILTERS';
const INDICATOR_PAYLOAD_SESSION_KEY = 'ONE_PAGER_INDICATOR_PAYLOAD';
const canUseSessionStorage = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const ExtendedReportView = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const portfolioName = usePortfolioName();
    const indicatorPayload = useSelector((state: any) => state.indicatorPayload.payload);

    const queryParams = new URLSearchParams(location.search);
    const from = queryParams.get('from') || '';
    const projectId = queryParams.get('id');
    const projectCode = queryParams.get('code');
    const isPMRType = (queryParams.get('isPMRType') || '').toLowerCase() === "true";

    const reportSections = getSections(from, isPMRType);

    const [storedProjectCode, setStoredProjectCode] = useState<string | null>(projectCode);
    const [storedPortfolioName, setStoredPortfolioName] = useState<string | null>(portfolioName || null);
    const projectCodeToUse = storedProjectCode || projectCode || '';
    const portfolioNameToUse = storedPortfolioName || portfolioName || '';

    const headerTitle = from !== 'project' ? t('one_pager_report.create_portfolio_report') + ' ' + portfolioNameToUse
        : t('one_pager_report.new_create_project_report') + ' ' + projectCodeToUse;
    const [storedHeaderTitle, setStoredHeaderTitle] = useState(headerTitle || '');

    const { labels } = useContext(TagFilterContext);
    const sectorsFromRedux = useSelector((state: any) => state.sectors);
    const divisionsFromRedux = useSelector((state: any) => state.divisions);
    const filterIndicatorsState = useSelector((state: any) => state.filterIndicators);
    const filterState = useSelector((state: any) => state.filter);
    const [reportData, setReportData] = useState<any>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [alertConfig, setAlertConfig] = useState<any>();

    const [sections, setSections] = useState(() => {
        const base = mapInitialCheckSections(reportSections);
        return base.map((s: any, idx: number) =>
            idx === 0
                ? { ...s, checked: true, locked: true }
                : s
        );
    });

    const selectedSectionsInOrder = sections.filter(section => section.checked);
    const canDownload = from === 'project' || Boolean(reportData);
    const displayHeaderTitle = storedHeaderTitle || headerTitle;
    const [storedLabels, setStoredLabels] = useState<string[]>(() => {
        if (!canUseSessionStorage()) return [];
        try {
            const cached = sessionStorage.getItem(MAPPED_FILTERS_SESSION_KEY);
            return cached ? JSON.parse(cached) : [];
        } catch (error) {
            sessionStorage.removeItem(MAPPED_FILTERS_SESSION_KEY);
            return [];
        }
    });

    const buildFiltersApplied = () => {
        try {
            const filters = indicatorPayload?.Filters || [];
            const sectorFilter = filters.find(f => f.field === 'SectorId');
            const divisionFilter = filters.find(f => f.field === 'DivisionId');

            const sectorLabelsFromPayload = sectorFilter && sectorFilter.value && Array.isArray(sectorFilter.value)
                ? sectorFilter.value
                    .map((id: number) => sectorsFromRedux.find((s: any) => s.id === id))
                    .filter(Boolean)
                    .map((s: any) => `${s.code} - ${s.description}`)
                    .join(', ')
                : null;

            const sectorLabelsFromState = (() => {
                const fromFilterIndicators = filterIndicatorsState && filterIndicatorsState.common && Array.isArray(filterIndicatorsState.common.sectors)
                    ? filterIndicatorsState.common.sectors
                    : null;

                const fromFilter = filterState && filterState.common && Array.isArray(filterState.common.sectors)
                    ? filterState.common.sectors
                    : null;

                const selected = fromFilterIndicators && fromFilterIndicators.length ? fromFilterIndicators : fromFilter;
                if (!selected || !selected.length) return null;
                return selected
                    .map((s: any) => (s.code ? `${s.code} - ${s.description}` : s.label || String(s.id)))
                    .join(', ');
            })();

            const sectorLabels = sectorLabelsFromPayload || sectorLabelsFromState || null;

            const divisionLabels = divisionFilter && divisionFilter.value && Array.isArray(divisionFilter.value)
                ? divisionFilter.value
                    .map((id: number) => divisionsFromRedux.find((d: any) => d.id === id))
                    .filter(Boolean)
                    .map((d: any) => `${d.code} - ${d.name}`)
                    .join(', ')
                : null;

            return [null, sectorLabels, divisionLabels];
        } catch (e) {
            return null;
        }
    };

    useEffect(() => {
        if (!labels || !labels.length || !canUseSessionStorage()) return;
        setStoredLabels(labels);
        try {
            sessionStorage.setItem(MAPPED_FILTERS_SESSION_KEY, JSON.stringify(labels));
        } catch (error) {
            sessionStorage.removeItem(MAPPED_FILTERS_SESSION_KEY);
        }
    }, [labels]);

    useEffect(() => {
        if (projectCode) {
            setStoredProjectCode(projectCode);
        }
    }, [projectCode]);

    useEffect(() => {
        if (storedProjectCode || !canUseSessionStorage()) return;

        const cachedProjectCode = sessionStorage.getItem(PROJECT_CODE_SESSION_KEY);
        if (cachedProjectCode) {
            setStoredProjectCode(cachedProjectCode);
        }
    }, [storedProjectCode]);

    useEffect(() => {
        if (!storedProjectCode || !canUseSessionStorage()) return;

        try {
            sessionStorage.setItem(PROJECT_CODE_SESSION_KEY, storedProjectCode);
        } catch (error) {
            sessionStorage.removeItem(PROJECT_CODE_SESSION_KEY);
        }
    }, [storedProjectCode]);

    useEffect(() => {
        if (portfolioName) {
            setStoredPortfolioName(portfolioName);
        }
    }, [portfolioName]);

    useEffect(() => {
        if (storedPortfolioName || !canUseSessionStorage()) return;

        const cachedPortfolioName = sessionStorage.getItem(PORTFOLIO_NAME_SESSION_KEY);
        if (cachedPortfolioName) {
            setStoredPortfolioName(cachedPortfolioName);
        }
    }, [storedPortfolioName]);

    useEffect(() => {
        if (!storedPortfolioName || !canUseSessionStorage()) return;

        try {
            sessionStorage.setItem(PORTFOLIO_NAME_SESSION_KEY, storedPortfolioName);
        } catch (error) {
            sessionStorage.removeItem(PORTFOLIO_NAME_SESSION_KEY);
        }
    }, [storedPortfolioName]);

    useEffect(() => {
        if (!portfolioNameToUse || !canUseSessionStorage()) return;

        try {
            sessionStorage.setItem(PORTFOLIO_NAME_SESSION_KEY, portfolioNameToUse);
        } catch (error) {
            sessionStorage.removeItem(PORTFOLIO_NAME_SESSION_KEY);
        }
    }, [portfolioNameToUse]);

    useEffect(() => {
        if (indicatorPayload || !canUseSessionStorage()) return;

        const cachedPayload = sessionStorage.getItem(INDICATOR_PAYLOAD_SESSION_KEY);
        if (!cachedPayload) return;

        try {
            const parsedPayload = JSON.parse(cachedPayload);
            dispatch(setPayload(parsedPayload));
        } catch (error) {
            sessionStorage.removeItem(INDICATOR_PAYLOAD_SESSION_KEY);
        }
    }, [indicatorPayload, dispatch]);

    useEffect(() => {
        if (!indicatorPayload || !canUseSessionStorage()) return;

        try {
            sessionStorage.setItem(INDICATOR_PAYLOAD_SESSION_KEY, JSON.stringify(indicatorPayload));
        } catch (error) {
            sessionStorage.removeItem(INDICATOR_PAYLOAD_SESSION_KEY);
        }
    }, [indicatorPayload]);

    useEffect(() => {
        if (headerTitle) {
            setStoredHeaderTitle(headerTitle);
        }
    }, [headerTitle]);

    useEffect(() => {
        if (storedHeaderTitle || !canUseSessionStorage()) return;

        const cachedHeaderTitle = sessionStorage.getItem(HEADER_TITLE_SESSION_KEY);
        if (cachedHeaderTitle) {
            setStoredHeaderTitle(cachedHeaderTitle);
        }
    }, [storedHeaderTitle]);

    useEffect(() => {
        if (!storedHeaderTitle || !canUseSessionStorage()) return;

        try {
            sessionStorage.setItem(HEADER_TITLE_SESSION_KEY, storedHeaderTitle);
        } catch (error) {
            sessionStorage.removeItem(HEADER_TITLE_SESSION_KEY);
        }
    }, [storedHeaderTitle]);

    useEffect(() => {
        if (indicatorPayload && from !== 'project') {
            handleGetDataExtendedReport()
        }
    }, [indicatorPayload])

    useEffect(() => {
        if (alertConfig === null && from === 'project') {
            const basePath = `/projectdetail?id=${projectId}`;
            history.push(basePath);
        }
    }, [alertConfig])

    useEffect(() => {
        if (!canUseSessionStorage()) return;

        const computedFilters = buildFiltersApplied();
        if (!computedFilters) return;

        try {
            sessionStorage.setItem(HEADER_FILTERS_SESSION_KEY, JSON.stringify(computedFilters));
        } catch (error) {
            sessionStorage.removeItem(HEADER_FILTERS_SESSION_KEY);
        }
    }, [indicatorPayload, sectorsFromRedux, divisionsFromRedux, filterIndicatorsState, filterState]);

    useEffect(() => {
        if (!reportData || !canUseSessionStorage()) return;

        const generalDataToPersist = {
            creationDate: reportData.creationDate,
            regions: reportData.regions,
            countries: reportData.countries,
        };

        try {
            sessionStorage.setItem(HEADER_GENERAL_DATA_SESSION_KEY, JSON.stringify(generalDataToPersist));
        } catch (error) {
            sessionStorage.removeItem(HEADER_GENERAL_DATA_SESSION_KEY);
        }
    }, [reportData]);

    const handleCheckboxChange = (id: string, isChecked: boolean) => {
        setSections(prev =>
            prev.map(card =>
                card.id === id
                    ? (card.locked ? card : { ...card, checked: isChecked })
                    : card
            )
        );
    };

    const handleDragEnd = (e: any) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;

        setSections(prev => {
            const oldIndex = prev.findIndex(item => item.id === active.id);
            let newIndex = prev.findIndex(item => item.id === over.id);

            if (oldIndex === -1 || newIndex === -1) {
                return prev;
            }

            if (prev[oldIndex].locked) {
                return prev;
            }

            if (prev[newIndex].locked) {
                const firstNotLocked = prev.findIndex(item => !item.locked);
                if (firstNotLocked === -1) return prev;
                newIndex = Math.max(firstNotLocked, 1);
            }

            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleGetDataExtendedReport = async () => {
        try {
            const data = await fetchExtendedReportFromPayload(indicatorPayload);
            setReportData(data);
        } catch (e) {
            console.error('Error in handleGetDataExtendedReport function:', e);
        }
    };

    const handleBackClick = () => {
        let basePath = 'home';

        if (from === 'project') {
            basePath += `?from=${from}&id=${projectId}&code=${projectCodeToUse}&isPMRType=${isPMRType}`;
        }

        history.push(basePath);
    }

    const downloadPdfReport = async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setAlertConfig(ERROR_IN_GENERATE_REPORT);
            return;
        }
        if (from === 'project') {
            try {
                await downloadExtendedProjectReport(projectId, selectedSectionsInOrder, isPMRType, setIsLoading);
                setAlertConfig(SUCESSFUL_GENERATED_PROJECT_REPORT);
            }
            catch (e) {
                setAlertConfig(ERROR_IN_GENERATE_REPORT);
            }
            return;
        }

        setIsLoading(true);
        try {
            await ensureReportData();
            const container = await waitForPreviewDOM();
            await handleGeneratePdf(container);
            setAlertConfig(SUCESSFUL_GENERATED_PROJECT_REPORT);
        } catch (error) {
            setAlertConfig(ERROR_IN_GENERATE_REPORT);
        } finally {
            setIsLoading(false);
        }
    }

    const ensureReportData = async () => {
        if (from === 'project') return null;
        if (reportData) return reportData;

        if (!indicatorPayload) {
            throw new Error('There are no parameters to generate the report.');
        }

        const data = await fetchExtendedReportFromPayload(indicatorPayload);
        setReportData(data);
        return data;
    };

    const waitForPreviewDOM = async () => {
        const MAX_ATTEMPTS = 20; // ~4s
        const INTERVAL_MS = 200;

        const hasPendingCanvas = (node: HTMLElement) => {
            const canvases = Array.from(node.querySelectorAll('canvas')) as HTMLCanvasElement[];
            if (!canvases.length) return false;
            return canvases.some(canvas => !canvas.width || !canvas.height);
        };

        const hasPages = (node: HTMLElement) => node.querySelectorAll('.pdf-page').length > 0;

        for (let i = 0; i < MAX_ATTEMPTS; i++) {
            const el = document.getElementById('download-preview') as HTMLElement | null;
            if (el && hasPages(el) && !hasPendingCanvas(el)) {
                return el;
            }
            await new Promise(r => setTimeout(r, INTERVAL_MS));
        }
        throw new Error('Could not prepare preview for PDF.');
    };

    const handleGeneratePdf = async (containerEl?: HTMLElement) => {
        const container = containerEl ?? document.getElementById('download-preview');
        if (!container) return;

        const pdf = new jsPDF('l', 'mm', 'a4');
        const pdfW = pdf.internal.pageSize.getWidth();
        const pdfH = pdf.internal.pageSize.getHeight();

        const pages = container.querySelectorAll('.pdf-page');

        for (let i = 0; i < pages.length; i++) {
            const element = pages[i] as HTMLElement;

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });

            if (!canvas.width || !canvas.height) continue;

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const ratio = Math.min(pdfW / canvas.width, pdfH / canvas.height);
            const imgW = canvas.width * ratio;
            const imgH = canvas.height * ratio;
            const x = (pdfW - imgW) / 2;
            const y = (pdfH - imgH) / 2;

            if (i > 0) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', x, y, imgW, imgH);
        }

        pdf.save('extended-report.pdf');
    };

    return (
        <div className='c-extended-report'>
            <Header detailProject />

            <div className='c-extended-report-header'>
                <button onClick={handleBackClick} className='btn btn-primary-text back-btn'>
                    <IconBack />{t('memory_help.button_back')}
                </button>
                <div className='c-extended-header-content'>
                    <h4 className='extended-header-title'>{displayHeaderTitle}</h4>
                    <div className='extended-header-filter-details'><MappedFilters data={labels && labels.length ? labels : storedLabels} /></div>
                </div>
            </div>
            <div className='c-extended-card-content'>
                <h2>{t('extended_report.header_extented_report_info')}</h2>
                <div>
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={sections.map((card) => card.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {sections.map(card => {
                                return (
                                    <SortableItem key={card.id} id={card.id} isDraggable={card.isDragging}>
                                        <ExtendedReportCard
                                            title={t(card.title)}
                                            content={t(card.content)}
                                            checked={card.checked}
                                            onChange={(checked) => handleCheckboxChange(card.id, checked)}
                                        />
                                    </SortableItem>
                                )
                            })
                            }
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
            <div className='c-btns'>
                <button
                    className='btn btn-primary'
                    onClick={() => downloadPdfReport()}>
                    <IconDownload />
                    {t('extended_report.download_pdf')}
                </button>
            </div>
            <BaseModal
                isOpen={isLoading}
                style={styles}
                className='downloading-report'
            >
                <SpinningLoader customStyle={{ marginTop: 0 }} customText={getLabelDownloadingReport(from)} />
            </BaseModal>

            {reportData && (
                <div
                    id="download-preview"
                    style={{ position: 'absolute', left: '-9999px', top: 0 }}
                >
                    <DownloadPreviewPDF
                        data={reportData}
                        order={selectedSectionsInOrder.map(card => card.id)}
                        isLoan={reportData.isLoan}
                        isIgr={reportData.isIgr}
                        isTcp={reportData.isTcp}
                        creationDate={reportData.creationDate}
                        regions={reportData.regions}
                        myPortfolio={reportData.myPortfolio}
                        countries={reportData.countries}
                        filtersApplied={buildFiltersApplied()}
                    />
                </div>
            )}

            {alertConfig && (
                <AutoDismissAlert
                    message={alertConfig.message}
                    title={alertConfig.title}
                    severity={alertConfig.severity}
                    duration={alertConfig.duration}
                    onClose={() => setAlertConfig(null)}
                />
            )}
        </div>
    )
}

export default ExtendedReportView
